'use client';

import { useEffect, useRef, useState } from 'react';

import type { CommunityChannelMessagePostLink } from '@ps/models/lib/community-channel';
import { cn } from '@ps/ui/utils';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

import { PostChatMessageView } from '../../../../posts/post-card/PostChatMessageView';
import { postCardDataFromMessageLink } from '../../../functions/links/postCardDataFromMessageLink';

interface Props {
  links: CommunityChannelMessagePostLink[];
  onRemove: (id: string) => void;
}

/**
 * Horizontal scroll strip of staged post-link cards in the composer.
 * Mirrors `ChannelComposerAttachmentStrip` in chrome — chevron-nudge
 * buttons appear when the strip overflows, X-remove button surfaces
 * on hover.
 *
 * Each card is the shared `PostChatMessageView` (the same one the
 * bubble uses) so the composer preview lines up exactly with what
 * the recipient will see.
 */
export function ChannelComposerLinkStrip({ links, onRemove }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      const maxScroll = el.scrollWidth - el.clientWidth;
      setCanScrollLeft(el.scrollLeft > 4);
      setCanScrollRight(el.scrollLeft < maxScroll - 4);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [links.length]);

  if (links.length === 0) return null;

  const nudge = (direction: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * 320, behavior: 'smooth' });
  };

  return (
    <div className="relative mt-2">
      <div
        ref={scrollRef}
        className="flex gap-2.5 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {links.map(link => (
          <ChannelComposerLinkTile
            key={link.id}
            link={link}
            onRemove={() => onRemove(link.id)}
          />
        ))}
      </div>
      {canScrollLeft ? (
        <ScrollNudgeButton direction="left" onClick={() => nudge(-1)} />
      ) : null}
      {canScrollRight ? (
        <ScrollNudgeButton direction="right" onClick={() => nudge(1)} />
      ) : null}
    </div>
  );
}

function ChannelComposerLinkTile({
  link,
  onRemove,
}: {
  link: CommunityChannelMessagePostLink;
  onRemove: () => void;
}) {
  return (
    <div className="group relative shrink-0">
      <PostChatMessageView
        post={postCardDataFromMessageLink(link, { isOwner: true })}
      />
      <button
        type="button"
        aria-label={`Remove ${link.title ?? 'post'}`}
        onClick={onRemove}
        className="text-content-1 bg-surface-1 absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full opacity-0 shadow-lg transition focus-visible:opacity-100 group-hover:opacity-100"
      >
        <X className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  );
}

function ScrollNudgeButton({
  direction,
  onClick,
}: {
  direction: 'left' | 'right';
  onClick: () => void;
}) {
  const Icon = direction === 'left' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      aria-label={direction === 'left' ? 'Scroll left' : 'Scroll right'}
      onClick={onClick}
      className={cn(
        'absolute top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white text-black shadow-md transition hover:scale-105',
        direction === 'left' ? 'left-1' : 'right-1',
      )}
    >
      <Icon className="h-4 w-4" aria-hidden />
    </button>
  );
}
